import { chatbotBase } from "../../config/api";

const API_BASE_URL = chatbotBase.replace(/\/$/, "");

type RequestMethod = "GET" | "POST" | "PUT" | "DELETE";

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

/**
 * Pull a readable message out of an error body. FastAPI sends either a plain
 * string `detail` or the app's structured `{ detail: { error: { message } } }`
 * envelope; both land here, and anything else falls back to the status line.
 */
const errorMessage = (payload: string, status: number): string => {
  const fallback = `Request failed with status ${status}`;
  if (!payload) return fallback;
  try {
    const parsed = JSON.parse(payload) as { detail?: unknown; error?: unknown };
    const { detail } = parsed;
    if (typeof detail === "string") return detail;
    const nested = (detail as { error?: { message?: string } } | undefined)?.error?.message;
    if (nested) return nested;
    if (typeof parsed.error === "string") return parsed.error;
    return fallback;
  } catch {
    return payload;
  }
};

export const requestJson = async <TResponse, TBody = unknown>(
  method: RequestMethod,
  path: string,
  body?: TBody,
  signal?: AbortSignal
): Promise<TResponse> => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const requestUrl = normalizedPath.startsWith("/compute-api/")
    ? normalizedPath
    : `${API_BASE_URL}${normalizedPath}`;

  const response = await fetch(requestUrl, {
    method,
    headers: {
      "Content-Type": "application/json"
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal
  });

  const payloadText = await response.text();
  if (!response.ok) {
    throw new ApiError(errorMessage(payloadText, response.status), response.status);
  }

  if (!payloadText) {
    return {} as TResponse;
  }

  return JSON.parse(payloadText) as TResponse;
};
